import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Chip, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { getPlans } from '../rest/BackendApi';
import { changePlanUrl, checkoutUrl, forgetSelectedPlan, formatPrice, getSelectedPlan, groupPlans, PlanCard } from '../business/Plans';
import { SimpleLoading } from './Loading';
import SupportLink from './SupportLink';
import StripeButton from './StripeButton';

const MOST_POPULAR_LEVEL = 'BUSINESS';

/**
 * - 'subscribe': no subscription yet, each plan starts Stripe Checkout with its trial
 * - 'change': the billing admin confirms the switch to a plan in the Stripe billing portal
 * - 'renew': the subscription ended, the billing admin picks a plan to start again in Stripe Checkout, without a trial
 * - 'view': someone else in the firm, who can't change the plan
 */
type Mode = 'subscribe' | 'change' | 'renew' | 'view';

function PlanAction(props: { mode: Mode; plan: Plan }) {
  const { mode, plan } = props;
  if (mode === 'view') {
    return null;
  }
  if (mode === 'change') {
    return (
      <StripeButton variant="outlined" fullWidth href={changePlanUrl(plan)}>
        Switch to this plan
      </StripeButton>
    );
  }
  if (mode === 'renew') {
    return (
      <StripeButton variant="contained" fullWidth href={checkoutUrl(plan)}>
        Renew with this plan
      </StripeButton>
    );
  }
  return (
    <StripeButton variant="contained" fullWidth href={checkoutUrl(plan)} onClick={forgetSelectedPlan}>
      {plan.trialDays ? `Start ${plan.trialDays}-day free trial` : 'Subscribe'}
    </StripeButton>
  );
}

function PlanCardView(props: { card: PlanCard; mode: Mode; selectedKey: string | null; currentLevel?: string }) {
  const { card, mode, selectedKey, currentLevel } = props;
  const selectedTier = card.tiers.find(tier => tier.lookupKey === selectedKey);
  const [plan, setPlan] = useState<Plan>(selectedTier || card.tiers[0]);
  const picked = selectedTier != null;
  const current = currentLevel === plan.level;
  // Plans sold in one size can't be changed to themselves, Business + can still change size. Any plan can be renewed.
  const showAction = mode === 'renew' || !(current && card.tiers.length === 1);

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={card.name}
      sx={{
        p: 3,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderWidth: picked ? 2 : 1,
        borderColor: picked ? 'primary.main' : 'divider',
      }}
    >
      <Box>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Typography component="h3" sx={{ fontSize: '1.15rem', fontWeight: 700 }}>
            {card.name}
          </Typography>
          {plan.level === MOST_POPULAR_LEVEL && <Chip size="small" label="Most popular" />}
          {picked && <Chip size="small" color="primary" label="Your pick" />}
          {current && <Chip size="small" color="success" variant="outlined" label={mode === 'renew' ? 'Previous plan' : 'Current plan'} />}
        </Stack>
        {mode === 'subscribe' && plan.trialDays && (
          <Typography variant="body2" sx={{ color: '#1e6b3a', fontWeight: 600, mt: 0.5 }}>
            {plan.trialDays}-day free trial, no card needed
          </Typography>
        )}
      </Box>

      <Box>
        <Typography component="span" sx={{ fontSize: '2.25rem', fontWeight: 700, lineHeight: 1 }}>
          {formatPrice(plan)}
        </Typography>
        <Typography component="span" color="text.secondary">
          {' '}
          per {plan.interval}, excl. GST
        </Typography>
      </Box>

      {card.tiers.length > 1 && (
        <Box>
          <Typography id={`${plan.level}-tiers`} variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            How many Xero connections?
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={plan.lookupKey}
            aria-labelledby={`${plan.level}-tiers`}
            onChange={(_, lookupKey) => lookupKey && setPlan(card.tiers.find(tier => tier.lookupKey === lookupKey) || plan)}
            sx={{ flexWrap: 'wrap' }}
          >
            {card.tiers.map(tier => (
              <ToggleButton key={tier.lookupKey} value={tier.lookupKey} sx={{ minWidth: 44, px: 1 }}>
                {tier.connections}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <Stack component="ul" gap={1} sx={{ listStyle: 'none', p: 0, m: 0, flexGrow: 1 }}>
        {plan.connections && <Feature text={`Up to ${plan.connections} Xero connections`} />}
        {plan.features.map(feature => (
          <Feature key={feature} text={feature} />
        ))}
      </Stack>

      {showAction && <PlanAction mode={mode} plan={plan} />}
    </Paper>
  );
}

function Feature(props: { text: string }) {
  return (
    <Stack component="li" direction="row" gap={1} alignItems="flex-start">
      <CheckIcon sx={{ color: '#1e6b3a', fontSize: 20, mt: '2px' }} />
      <Typography variant="body2">{props.text}</Typography>
    </Stack>
  );
}

export default function Pricing(props: { mode: Mode; currentLevel?: string }) {
  const [plans, setPlans] = useState<Plan[] | null | undefined>(undefined);

  useEffect(() => {
    getPlans()
      .then(setPlans)
      .catch(() => setPlans(null));
  }, []);

  if (plans === undefined) {
    return <SimpleLoading message="Loading plans" />;
  }
  if (plans === null || plans.length === 0) {
    return (
      <Typography color="text.secondary">
        We couldn't load the plans. Refresh the page, or <SupportLink text="contact support" />.
      </Typography>
    );
  }

  const selectedKey = props.mode === 'subscribe' ? getSelectedPlan() : null;
  const maxConnections = Math.max(...plans.map(plan => plan.connections || 0));

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 2, alignItems: 'stretch' }}>
        {groupPlans(plans).map(card => (
          <PlanCardView key={card.name} card={card} mode={props.mode} selectedKey={selectedKey} currentLevel={props.currentLevel} />
        ))}
      </Box>
      {props.mode === 'view' && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Ask your firm's billing admin to change the plan.
        </Typography>
      )}
      <Typography id="contactUsCustomPlan" sx={{ textAlign: 'center', mt: 3 }}>
        More than {maxConnections} Xero organisations or specific needs? <SupportLink text="Contact us" /> for a custom plan.
      </Typography>
    </Box>
  );
}
